// Route configuration
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { ROUTES } from '@constants';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicRoute } from './PublicRoute';
import { AdminRoute } from './AdminRoute';

// Lazy load pages
import { lazy, Suspense } from 'react';

const Login = lazy(() => import('@pages/Auth/Login'));
const Register = lazy(() => import('@pages/Auth/Register'));
const OryRegistration = lazy(() => import('@pages/Auth/OryRegistration'));
const OryCallback = lazy(() => import('@pages/Auth/OryCallback'));
const Recovery = lazy(() => import('@pages/Auth/Recovery'));
const Verification = lazy(() => import('@pages/Auth/Verification'));
const Dashboard = lazy(() => import('@pages/Dashboard/Dashboard'));
const Settings = lazy(() => import('@pages/Account/Settings'));
const ManageAccount = lazy(() => import('@pages/Account/ManageAccount'));
const AdminSettings = lazy(() => import('@pages/Admin/Settings'));
const NotFound = lazy(() => import('@pages/NotFound/NotFound'));
const HomePage = lazy(() => import('../pages/Home'));
const ApplicationLayout = lazy(() => import('../pages/Application/ApplicationLayout'));

// Loading component
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    Loading...
  </div>
);

// Router configuration
const router = createBrowserRouter([
  {
    path: ROUTES.HOME,
    element: <Navigate to={ROUTES.LOGIN} replace />,
  },
  {
    element: <PublicRoute />,
    children: [
      {
        path: ROUTES.LOGIN,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Login />
          </Suspense>
        ),
      },
      {
        path: ROUTES.REGISTER,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Register />
          </Suspense>
        ),
      },
      {
        path: '/registration',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <OryRegistration />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: ROUTES.ORY_CALLBACK,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <OryCallback />
      </Suspense>
    ),
  },
  {
    path: ROUTES.RECOVERY,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Recovery />
      </Suspense>
    ),
  },
  {
    path: ROUTES.VERIFICATION,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Verification />
      </Suspense>
    ),
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: '/home',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <HomePage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.APPLICATION,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ApplicationLayout />
          </Suspense>
        ),
      },
      {
        path: ROUTES.DASHBOARD,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Dashboard />
          </Suspense>
        ),
      },
      {
        path: ROUTES.ACCOUNT_SETTINGS,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Settings />
          </Suspense>
        ),
      },
      {
        path: ROUTES.ACCOUNT_MANAGE,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ManageAccount />
          </Suspense>
        ),
      },
    ],
  },
  {
    element: <AdminRoute />,
    children: [
      {
        path: ROUTES.ADMIN_SETTINGS,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AdminSettings />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <NotFound />
      </Suspense>
    ),
  },
]);

export const AppRouter = () => {
  return <RouterProvider router={router} />;
};
